<template>
  <b-card class="OvertimeCard mb-5">
    <b-card-title title-tag="h2" class="text-center mb-5">OVERTIME APPLICATION FORM</b-card-title>

    <div class="banner mb-3">Employee Information</div>
    <div class="mb-5">
      <b-col cols="12" lg="6">
        <ListDetails
          :values="employeeDetails"
          :options="employeeDetailsOptions"
        />
      </b-col>
    </div>

    <div class="banner dark text-white mb-3">Details of Overtime Application</div>

    <div :class="readonly ? 'mb-2' : 'row mb-2'">
      <template v-if="!readonly">
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-2">
          Date
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <div
          class="col-12 col-sm-6 col-lg-4 mb-1"
        >
          <flat-pickr
            ref="date"
            id="date"
            class="form-control no-outline border-dark rounded-0"
            v-model="formObject.date"
            :disabled="isLoadingRequest"
            :config="formOptions.date"
          />
        </div>
      </template>
      <InputField
        v-else
        label="Date"
        :value="readOnlyForm.date"
        :options="formOptions.date"
        :is-required="true"
        :disabled="isLoadingRequest"
      />
    </div>
    <div class="mb-2">
      <InputField
        v-if="readonly"
        label="Type of Overtime"
        :value="readOnlyForm.type"
        :options="formOptions.type"
        :is-required="true"
        :disabled="isLoadingRequest"
        @change="onChangeType"
      />
      <InputField
        v-else
        label="Type of Overtime"
        v-model="formObject.type"
        :options="formOptions.type"
        :is-required="true"
        :disabled="isLoadingRequest"
        @change="onChangeType"
      />
    </div>
    <b-row class="my-5 mt-lg-0">
      <template v-if="readonly">
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-2">
          Start Time
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <div class="col-12 col-sm-6 col-lg-2 mb-1">
          <input
            readonly
            type="text"
            :value="readOnlyForm.startTime"
            class="form-control-plaintext no-outline border-dark border px-2 rounded-0"
          />
        </div>
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-1">
          To
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <div
          class="col-12 col-sm-6"
          :class="{ 'col-lg-3': isNextDay, 'col-lg-2': !isNextDay }"
        >
          <input
            readonly
            type="text"
            :value="readOnlyForm.toTime"
            class="form-control-plaintext no-outline border-dark border px-2 rounded-0"
          />
        </div>
      </template>
      <template v-else>
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-2">
          Start Time
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <div class="col-12 col-sm-6 col-lg-2 mb-1">
          <flat-pickr
            ref="startTime"
            id="start_time"
            class="no-outline border-dark rounded-0"
            v-model="formObject.startTime"
            :disabled="timeFieldsDisabled || isLoadingRequest"
            :config="formOptions.startTime"
          />
        </div>
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-1">
          To
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <div
          class="col-12 col-sm-6"
          :class="{ 'col-lg-4': isNextDay, 'col-lg-2': !isNextDay }"
        >
          <div class="input-group">
            <flat-pickr
              ref="toTime"
              id="to_time"
              class="no-outline border-dark rounded-0"
              v-model="formObject.toTime"
              :disabled="timeFieldsDisabled || isLoadingRequest"
              :config="formOptions.toTime"
            />
            <div
              v-if="isNextDay"
              class="input-group-append"
              v-b-tooltip.hover
              title="Until next day"
            >
              <span
                class="input-group-text font-weight-bold bg-light border-dark rounded-0"
              >
                {{ $fdr(nextDayDate, true) }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </b-row>
    <div class="mb-2">
      <InputField
        label="Total Number of Hour/s"
        :value="totalHoursComputed"
        :options="formOptions.totalHours"
      >
        <div
          v-if="showTotalHoursMsg"
          class="text-danger"
        >
          Total Hours must be at least 1 hour.
        </div>
      </InputField>
    </div>
    <div class="mb-5">
      <InputField
        label="Night Differential (If Applicable)"
        :value="nightDiff"
        :options="formOptions.nightDiff"
      />
    </div>
    <div class="mb-5">
      <InputField
        label="Details of Work Done"
        v-model="formObject.details"
        :options="formOptions.details"
        :is-required="true"
        :disabled="isLoadingRequest"
      />
    </div>

    <!-- display of approval status when viewing only -->
    <template v-if="isSubmitted">
      <div class="banner dark text-white mb-3">Verification and Approval</div>
      <div class="row mb-2">
        <InputField
          label="Manager"
          :options="formOptions.managerName"
          :value="readOnlyForm.manager"
        />
        <InputField
          label="Date"
          :options="formOptions.managerApprovalDate"
          :value="approvalDate"
        />
      </div>
      <div class="row mb-2">
        <InputField
          label="Status"
          :value="$get(currentStatus, 'status', '')"
          :options="formOptions.managerApproval"
        />
      </div>
      <div class="row">
        <InputField
          v-if="hasComments"
          label="Comments"
          :options="formOptions.managerComments"
          :value="managerComments"
        />
      </div>
    </template>

    <b-col
      v-if="!noSubmit"
      cols="12"
      md="6"
      offset-md="3"
    >
      <b-button
        squared
        size="lg"
        block
        :disabled="!enableSubmitBtn || isModalShowing"
        @click="showConfirmSubmit"
      >
        SUBMIT FOR APPROVAL
      </b-button>
    </b-col>
    <Modal
      v-if="!noSubmit"
      v-bind.sync="confirmModalOptions"
      @okBtn="submitBtn"
    >
      <template slot="default">
        <h4 class="mb-5">Are you sure you want to submit the form?</h4>

        <div class="row mb-2">
          <InputField
            :value="displayObject.date"
            v-bind="modalFields.date"
          />
          <InputField
            :value="displayObject.type"
            v-bind="modalFields.type"
          />
        </div>
        <div class="row mb-2">
          <InputField
            :value="displayObject.totalHours"
            v-bind="modalFields.totalHours"
          />
          <InputField
            :value="displayObject.time"
            v-bind="modalFields.time"
          />
        </div>
        <div class="row mb-2">
          <InputField
            :value="displayObject.nightDiff"
            v-bind="modalFields.nightDiff"
          />
        </div>
        <div class="row mb-2">
          <InputField
            :value="displayObject.details"
            v-bind="modalFields.details"
          />
        </div>
      </template>
    </Modal>
  </b-card>
</template>
<script>
import { mapGetters } from 'vuex';
import ListDetails from '@/components/ListDetails';
import InputField from '@/components/InputField';
import Modal from '@/components/Modal';

export default {
  name: 'OvertimeCard',
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
    noSubmit: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    others: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    ListDetails,
    InputField,
    Modal,
  },
  data() {
    return {
      timeFieldsDisabled: true,
      formObject: {
        date: this.$fd(new Date()),
        details: '',
        startTime: null,
        toTime: null,
      },
      confirmModalOptions: {
        show: false,
      },
    };
  },
  watch: {
    overtimeData: {
      immediate: false,
      handler: 'displayOvertimeData',
    },
  },
  computed: {
    isApproval() {
      return this.$get(this.others, 'forApproval', true);
    },
    loadingEmployee() {
      return this.$get(this.others, 'loadingEmployee', false);
    },
    typesOfOvertime() {
      return this.$get(this.others, 'typesOfOvertime', []);
    },
    showTotalHoursMsg() {
      return this.totalHours < 1
        && this.formObject.startTime
        && this.formObject.toTime;
    },
    enableSubmitBtn() {
      // Enable submit button if form is complete or not loading
      const hasDate = Boolean(this.formObject.date);
      const hasType = Boolean(this.formObject.type);
      const hasDetails = !!String(this.$get(this.formObject, 'details', '')).trim();
      const { startTime, toTime } = this.formObject;
      const hasStart = !this.$ie(startTime);
      const hasEnd = !this.$ie(toTime);
      const isAtLeast1Hr = this.totalHours > 0;

      return hasDate
        && hasType
        && hasStart
        && hasEnd
        && hasDetails
        && !this.isLoadingRequest
        && isAtLeast1Hr;
    },
    isModalShowing() {
      return this.$get(this.confirmModalOptions, 'show', false);
    },
    isSubmitted() {
      return !this.$ie(this.currentStatus);
    },
    employeeFields() {
      const defaultOptions = {
        size: 'sm',
        readonly: true,
        modifiers: ['text-left-align'],
        options: {
          labelColsSm: 4,
          labelCols: 12,
        },
      };
      const fieldNames = ['ID Number', 'Name', 'Position', 'Department', 'Team'];
      return fieldNames.map((field) => {
        let key = this.$snake(field);
        switch (field) {
          case 'ID Number':
            key = 'employee_no';
            break;
          case 'Name':
            key = 'full_name';
            break;
          default: break;
        }
        return {
          ...defaultOptions, label: field, value: '', key,
        };
      });
    },
    employeeDetails() {
      const details = {};
      this.employeeFields.forEach((field) => {
        const detailsToShow = [
          'full_name',
          'employee_no',
          'department',
          'team',
          'position',
        ];
        if (detailsToShow.includes(field.key)) {
          if (this.loadingEmployee) {
            details[field.key] = 'loading user data...';
          } else {
            details[field.key] = this.employee[field.key] || '';
          }
        }
      });
      return details;
    },
    employeeDetailsOptions() {
      return {
        labelClass: 'font-weight-bold',
        fields: this.employeeFields,
      };
    },
    defaultOptions() {
      return {
        tagClass: 'row',
        labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-2',
        inputClass: 'form-control no-outline border-dark border-bottom rounded-0',
        type: 'text',
      };
    },
    defaultTimeInputOptions() {
      return {
        disableMobile: true,
        enableTime: true,
        dateFormat: 'H:i',
        altFormat: 'h:i K',
        altInput: true,
      };
    },
    approvalDate() {
      const d = this.$get(this.currentStatus, 'approvalDate', '');
      if (!d) {
        return '';
      }
      return this.$fd(d, 'MMM D YYYY - h:mm A');
    },
    hasComments() {
      return !this.$ie(this.managerComments);
    },
    managerComments() {
      return this.$get(this.overtimeData, 'comments', '');
    },
    formOptions() {
      if (this.readonly) {
        const readonlyDefault = {
          tagClass: 'row',
          labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-2',
          inputClass: 'form-control-plaintext no-outline border-dark border px-2 rounded-0',
          type: 'text',
        };
        return {
          date: {
            ...readonlyDefault,
            id: 'date',
            readonly: true,
            input: {
              modifiers: 'col-12 col-sm-6 col-lg-4',
            },
          },
          type: {
            ...readonlyDefault,
            id: 'type',
            readonly: true,
            input: {
              modifiers: 'col-12 col-sm-6 col-lg-4',
            },
          },
          startTime: {
            ...readonlyDefault,
            id: 'startTime',
            tagClass: 'row col-12 col-lg-3',
            readonly: true,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg',
            input: {
              modifiers: 'col-12 col-sm-6 col-lg mb-1',
            },
          },
          toTime: {
            ...readonlyDefault,
            id: 'toTime',
            tagClass: 'row col-12 col-lg-3',
            readonly: true,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-3',
            input: {
              modifiers: 'col-12 col-sm-6 col-lg',
            },
          },
          totalHours: {
            ...readonlyDefault,
            id: 'total_hours',
            type: 'text',
            readonly: true,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-4',
            input: {
              modifiers: 'col-12 col-sm-6 col-lg-2',
            },
          },
          nightDiff: {
            ...readonlyDefault,
            id: 'night_diff',
            type: 'text',
            readonly: true,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-4',
            input: {
              modifiers: 'col-12 col-sm-6 col-lg-2',
            },
          },
          details: {
            ...readonlyDefault,
            id: 'details',
            type: 'textarea',
            labelClass: 'font-weight-bold mb-0 col-12',
            inputClass: 'no-outline border-dark border rounded-0 notes',
            plaintext: true,
            readonly: true,
            input: {
              modifiers: 'col-12',
            },
          },
          managerName: {
            ...readonlyDefault,
            id: 'manager-name',
            readonly: true,
            tagClass: 'row col-6',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-4 col-lg-6',
            input: {
              modifiers: 'col-12 col-sm-8 col-lg',
            },
          },
          managerApproval: {
            ...readonlyDefault,
            id: 'manager-approval',
            readonly: true,
            tagClass: 'row col-6',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-4 col-lg-6',
            input: {
              modifiers: 'col-12 col-sm-8 col-lg',
            },
          },
          managerApprovalDate: {
            ...readonlyDefault,
            id: 'manager-approval-date',
            readonly: true,
            tagClass: 'row col col-lg',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-4 col-lg-2',
            input: {
              modifiers: 'col-12 col-sm-8 col-lg-10',
            },
          },
          managerComments: {
            ...readonlyDefault,
            id: 'manager-comments',
            readonly: true,
            plaintext: true,
            type: 'textarea',
            tagClass: 'row col pr-0',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-2 col-lg-3',
            inputClass: 'mini border border-dark p-2',
            input: {
              modifiers: 'col-12 col-sm-6 col-md-10 col-lg-9 pl-2 pr-0',
            },
          },
        };
      }

      return {
        date: {
          disableMobile: true,
          defaultDate: new Date(),
          dateFormat: 'Y-m-d',
          altFormat: 'F j Y, l',
          altInput: true,
        },
        type: {
          ...this.defaultOptions,
          id: 'type',
          type: 'select',
          placeholder: '-- Please select type of overtime --',
          selectOptions: this.typesOfOvertime.map(item => ({
            text: item.name,
            value: item.id,
          })),
          input: {
            modifiers: 'col-12 col-sm-6 col-lg-4',
          },
        },
        startTime: {
          ...this.defaultTimeInputOptions,
          noCalendar: true,
          disabled: this.startTimeDisabled,
        },
        toTime: {
          ...this.defaultTimeInputOptions,
          noCalendar: true,
        },
        totalHours: {
          ...this.defaultOptions,
          id: 'total_hours',
          type: 'text',
          readonly: true,
          labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-4',
          input: {
            modifiers: 'col-12 col-sm-6 col-lg-2',
          },
        },
        nightDiff: {
          ...this.defaultOptions,
          id: 'night_diff',
          type: 'text',
          readonly: true,
          labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-4',
          input: {
            modifiers: 'col-12 col-sm-6 col-lg-2',
          },
        },
        details: {
          ...this.defaultOptions,
          id: 'details',
          type: 'textarea',
          labelClass: 'font-weight-bold mb-0 col-12',
          inputClass: 'form-control no-outline border-dark border-bottom rounded-0 notes',
          trim: true,
          input: {
            modifiers: 'col-12',
          },
        },
      };
    },
    modalFields() {
      const commonOptions = {
        readonly: true,
        tagClass: 'row col-12 col-lg-6',
        labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-3',
        inputClass: 'form-control-plaintext no-outline border-dark bg-none border rounded-0 px-1',
        type: 'text',
        input: {
          modifiers: 'col col-lg-9',
        },
      };
      return {
        date: {
          label: 'Date',
          options: {
            ...commonOptions,
            id: 'modal_date',
          },
        },
        type: {
          label: 'Type of Overtime',
          options: {
            id: 'modal_type',
            ...commonOptions,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-5',
            input: {
              modifiers: 'col col-lg',
            },
          },
        },
        time: {
          label: 'Time',
          options: {
            ...commonOptions,
            id: 'modal_time',
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-2',
            input: {
              modifiers: 'col col-lg-10',
            },
          },
        },
        totalHours: {
          label: 'Total Number of Hour/s',
          options: {
            ...commonOptions,
            id: 'modal_totalHours',
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-8',
            input: {
              modifiers: 'col col-lg-4',
            },
          },
        },
        nightDiff: {
          label: 'Night Differential (If Applicable)',
          options: {
            ...commonOptions,
            id: 'modal_nightDiff',
            tagClass: 'row col-12',
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-6',
            input: {
              modifiers: 'col-12 col-sm-6 col-lg-2',
            },
          },
        },
        details: {
          label: 'Details of Work Done',
          options: {
            ...commonOptions,
            id: 'modal_details',
            plaintext: true,
            type: 'textarea',
            tagClass: 'row col-12',
            labelClass: 'font-weight-bold mb-0 col-12',
            inputClass: 'form-control-plaintext no-outline border-dark notes border rounded-0 px-1',
            input: {
              modifiers: 'col-12',
            },
          },
        },
      };
    },
    displayObject() {
      // get type name
      const selectedTypeID = this.$get(this.formObject, 'type', -1);
      const typeObj = this.typesOfOvertime.find(item => Number(item.id) === Number(selectedTypeID));
      const type = this.$get(typeObj, 'name', '-');

      // Get concatenated start and to time
      const formattedTimes = [
        this.$fto(this.startTimeDate),
        this.$fd(this.toTimeDate, this.isNextDay ? 'hh:mm A (MMMM D YYYY)' : 'hh:mm A'),
      ];
      const time = formattedTimes.join(' - ');
      return {
        type,
        time,
        details: this.$get(this.formObject, 'details', '-'),
        totalHours: this.$get(this, 'totalHoursComputed', '0.00'),
        nightDiff: this.$get(this, 'nightDiff', '0.00'),
        date: this.$fd(this.formObject.date, 'MMM DD YYYY, dddd'),
      };
    },
    overtimeData() {
      return this.$get(this.others, 'overtimeData', {});
    },
    currentStatus() {
      const status = this.$get(this.overtimeData, 'status', null);
      const statusId = this.$get(this.overtimeData, 'statusId', null);
      const approvalDate = this.$get(this.overtimeData, 'approvalDate', null);

      return !statusId ? null : {
        approvalDate,
        status: this.$startCase(status),
        id: statusId,
      };
    },
    totalHoursComputed() {
      const n = this.totalHours < 0 ? 0 : this.totalHours;
      return Number(n).toFixed(2);
    },
    shiftScheduleData() {
      const ssArr = this.$get(this.employee, 'shift_schedule_arr', []);
      const ssStr = this.$get(this.employee, 'shift_schedule_str', '');
      return {
        schedule: ssArr,
        str: ssStr,
      };
    },
    startTimeDate() {
      const dateStr = this.$get(this.formObject, 'date', null);
      const timeStr = this.$get(this.formObject, 'startTime', null);
      if (this.$ie(timeStr)) {
        return null;
      }

      const [hh, mm] = timeStr.split(':');

      return this.$cdst(dateStr, hh, mm);
    },
    toTimeDate() {
      const dateStr = this.$get(this.formObject, 'date', null);
      const timeStr = this.$get(this.formObject, 'toTime', null);
      if (this.$ie(timeStr)) {
        return null;
      }

      const [hh, mm] = timeStr.split(':');
      const dateObj = this.$cdst(dateStr, hh, mm);

      // if to time is after start time, it is the next day
      if (this.$dateComp(this.startTimeDate, dateObj) === -1) {
        return this.$addDays(dateObj, 1);
      }
      return dateObj;
    },
    isNextDay() {
      if (this.$ie(this.startTimeDate) || this.$ie(this.toTimeDate)) {
        return false;
      }
      const dateOnlyStart = this.$cdst(this.startTimeDate, 0, 0, 0);
      const dateOnlyTo = this.$cdst(this.toTimeDate, 0, 0, 0);
      return this.$dateComp(dateOnlyStart, dateOnlyTo) === 1;
    },
    nextDayDate() {
      if (this.$ie(this.toTimeDate)) {
        return null;
      }
      return this.$cdst(this.toTimeDate, 0, 0, 0);
    },
    totalHours() {
      return this.$diffMin(this.toTimeDate, this.startTimeDate) / 60;
    },
    nightDiff() {
      /**
       * Get night differential range hours
       * compute:
       * - check if start < 10PM and end is > 6AM (automatic 8hours value)
       * - compute difference 10PM - end time (if end time is before 6am)
       */
      // Set Night Diff (10PM - 6AM)
      const startedTime = this.startTimeDate;
      const endTime = this.toTimeDate;
      const tenPM = this.$cdst(startedTime, 22, 0, 0);
      const sixAM = this.$cdst(this.$addDays(startedTime, 1), 6, 0, 0);
      const startIsBefore10 = this.$dateComp(startedTime, tenPM) === 1;
      const endIsAfter6 = this.$dateComp(sixAM, endTime) === 1;

      const sixAMx = this.$cdst(startedTime, 6, 0, 0);
      const endIsBefore6am = this.$dateComp(endTime, sixAMx) === 1;
      const startIsBefore6am = this.$dateComp(startedTime, sixAMx) === 1;

      let nightDiffHrs = 0;
      let nightDiffDeductions = 0;
      // Check if start < 10PM and end is > 6AM and  (automatic 8hours value)
      if (startIsBefore10 && endIsAfter6) {
        nightDiffHrs = 8;
      } else {
        // Condition for 12am to 6am(tom) night diff
        if (startedTime <= sixAMx && endTime >= tenPM) {
          nightDiffDeductions = this.$diffMin(tenPM, sixAMx) / 60;
        // Condition for 12am to 6am night diff
        } else if (startIsBefore6am && endIsBefore6am) {
          nightDiffDeductions = 0;
        } else if (startedTime <= sixAMx && endTime >= sixAMx) {
          nightDiffDeductions = this.$diffMin(endTime, sixAMx) / 60;
        } else if (!startIsBefore10 && !endIsAfter6) {
          nightDiffDeductions = 0;
        // Condition for 10pm to 6am(tom) night diff
        } else if (startedTime >= sixAMx && endTime <= tenPM) {
          nightDiffDeductions = this.$diffMin(endTime, startedTime) / 60;
        } else if (startedTime <= tenPM && endTime <= sixAM) {
          nightDiffDeductions = this.$diffMin(tenPM, startedTime) / 60;
        } else if (startedTime >= tenPM && endTime >= sixAMx) {
          nightDiffDeductions = this.$diffMin(endTime, sixAM) / 60;
        }
        nightDiffHrs = (this.$diffMin(endTime, startedTime) / 60) - nightDiffDeductions;
      }

      return (nightDiffHrs || 0).toFixed(2);
    },
    readOnlyForm() {
      const { date } = this.formObject;
      const type = this.$get(this.others, 'overtimeData.type', '');
      const toTimeFormat = this.isNextDay ? 'hh:mm A (MMMM D YYYY)' : 'hh:mm A';
      const manager = this.$get(this.others, 'overtimeData.manager', '');

      return {
        type,
        manager,
        date: this.$fdr(date, true),
        startTime: this.$fto(this.startTimeDate),
        toTime: this.$fd(this.toTimeDate, toTimeFormat),
      };
    },
    ...mapGetters(['isLoadingRequest']),
  },
  methods: {
    /**
     * Show confirmation dialog asking user if sure to submit timesheet for approval
     */
    showConfirmSubmit() {
      this.confirmModalOptions = {
        show: true,
        size: 'lg',
        title: 'SUBMIT OVERTIME APPLICATION',
        cancelBtnOptions: {
          variant: 'outline-secondary',
        },
        okBtnOptions: {
          variant: 'primary',
          text: 'OK, Submit my application',
        },
      };
    },
    resetTimeInputs() {
      const sdInstance = this.$get(this.$refs, 'startTime.$el._flatpickr', {});
      const tdInstance = this.$get(this.$refs, 'toTime.$el._flatpickr', {});
      // set time values as blank
      sdInstance.setDate(null);
      tdInstance.setDate(null);
    },
    onChangeType(val) {
      this.timeFieldsDisabled = !val;
    },
    submitBtn() {
      const data = {
        ...this.formObject,
        startTime: this.$fts(this.startTimeDate),
        toTime: this.$fts(this.toTimeDate),
        date: this.$fts(this.formObject.date),
      };

      this.$emit('submit', data);
    },
    resetForm() {
      this.formObject = {
        date: this.$fd(new Date()),
        details: '',
        type: '',
        startTime: null,
        toTime: null,
      };

      this.resetTimeInputs();
    },
    displayOvertimeData() {
      if (this.readonly) {
        const OTData = this.$get(this.others, 'overtimeData', {});
        const d = this.$get(OTData, 'recordDate', null);
        const type = this.$get(OTData, 'typeId', '-');
        const startTime = this.$get(OTData, 'startTime', null);
        const endTime = this.$get(OTData, 'endTime', null);

        const details = this.$get(OTData, 'details', null);

        this.formObject = {
          type,
          details,
          startTime: startTime ? this.$fto(startTime, true) : null,
          toTime: endTime ? this.$fto(endTime, true) : null,
          date: d ? this.$fd(d) : null,
        };
      }
    },
  },
};
</script>
